import {parseMetafieldValue} from '@shopify/hydrogen';
import {
  Metafield,
  ProductVariant,
} from '@shopify/hydrogen/storefront-api-types';
import {PartialDeep} from 'type-fest';
import {
  ProductVariantMetafieldKeyType,
  ShopifyProductVariantMetafieldKeyValueType,
} from '../shopifyGraphql/products';

export function getShowcasedVariant(
  variants: Array<ProductVariant>,
  sponsoredProductVariantId?: string,
  partnerOnly = false,
) {
  if (sponsoredProductVariantId) {
    const sponsoredVariant = variants.find(
      (variant) => variant.id === sponsoredProductVariantId,
    );
    if (sponsoredVariant && getIsVariantAvailableForSale(sponsoredVariant)) {
      return sponsoredVariant;
    }
  }

  return getCheapestAvailableVariant(variants, partnerOnly);
}

export function getCheapestAvailableVariant(
  variants: ReadonlyArray<ProductVariant>,
  partnerOnly = false,
): ProductVariant {
  const cheapestVariants = variants.reduce(
    (cheapest, current) => {
      const currentMetafieldsKeyValue = getShopifyProductVariantMetafields(
        current.metafields,
      );

      const key = currentMetafieldsKeyValue.sellerShopIsPartner
        ? 'partner'
        : 'nonPartner';

      // current isn't available for sale, so it can't be the cheapest
      if (!getIsVariantAvailableForSale(current)) {
        return cheapest;
      }

      // there is no cheapest yet, so the current is the cheapest
      if (!cheapest[key]) {
        cheapest[key] = current;
        return cheapest;
      }

      // pick the cheapest of the two
      cheapest[key] =
        Number(cheapest[key].price.amount) < Number(current.price.amount)
          ? cheapest[key]
          : current;
      return cheapest;
    },
    {partner: null, nonPartner: null} as {
      partner?: ProductVariant;
      nonPartner?: ProductVariant;
    },
  );
  // return the cheapest partner or non-partner when there is no
  // available partner
  return cheapestVariants.partner || partnerOnly
    ? cheapestVariants.partner
    : cheapestVariants.nonPartner;
}

export function getIsVariantAvailableForSale(variant: ProductVariant) {
  const {sellerShopProductIsInStock, sellerShopIsPartner} =
    getShopifyProductVariantMetafields(variant.metafields);

  return (
    sellerShopProductIsInStock &&
    (!sellerShopIsPartner || variant.availableForSale)
  );
}

/*
Get the variant that from the TwlShop (Must be available for sale)
*/
export function getViableTwlShopVariant(variants: readonly ProductVariant[]) {
  return variants.find(
    (variant) =>
      variant.title.trim().toLocaleLowerCase() ===
        'The Whisky List Shop'.toLocaleLowerCase() &&
      getIsVariantAvailableForSale(variant),
  );
}

function getMetafieldValueFromMetafields<T>(
  fieldKey: ProductVariantMetafieldKeyType,
  metafields: PartialDeep<Metafield>[],
): T | null {
  const metafield =
    metafields && metafields.find((metafield) => metafield?.key === fieldKey);
  return metafield && (parseMetafieldValue(metafield) as T);
}

export function getShopifyProductVariantMetafields(
  metafields: PartialDeep<Metafield>[],
): ShopifyProductVariantMetafieldKeyValueType {
  return Object.freeze({
    productTitle: getMetafieldValueFromMetafields<string>(
      'productTitle',
      metafields,
    ),
    sellerShopIsPartner: getMetafieldValueFromMetafields<boolean>(
      'sellerShopIsPartner',
      metafields,
    ),
    sellerShopProductIsInStock: getMetafieldValueFromMetafields<boolean>(
      'sellerShopProductIsInStock',
      metafields,
    ),
    sellerNote: getMetafieldValueFromMetafields<string>(
      'sellerNote',
      metafields,
    ),
    shopId: getMetafieldValueFromMetafields<string>(
      'external_shop_id',
      metafields,
    ),
  });
}
